// Material draußen (seit E3b, O65 – Manuels Wunsch, Gernots Entscheidung vom
// 22.10.2026): was die Seite „Material“ und der Knopf in jeder Station
// rechnen, ohne React.
//
//  - Die Standardliste kommt vom Server (GET /api/doku/standardmaterial:
//    die aktiven Einträge „zur Auswahl“ – Claudes Vorschlag 2 im Plan E3b)
//    und liegt am Gerät, damit „Material“ auch im Keller ohne Netz geht. Sie
//    wird bei jedem Abgleich ganz ersetzt; ohne Netz bleibt die letzte.
//    Seit E3c (S5) kommt mit ihr die Adresse von K2 der Firma („Dachplanung
//    starten“) und liegt ebenso am Gerät – beides mit der Firma, für die es
//    geholt wurde (O86: abmelden oder eine andere Firma – beides kommt weg).
//  - Einträge mit derselben Gruppe sind EINE Kachel mit Varianten
//    („Kabelkanal“ → 40×60, 60×60 …); ohne Gruppe ist jeder Eintrag seine
//    eigene Kachel. Reihenfolge wie vom Server (nach Gruppe, dann
//    Bezeichnung).
//  - Mengen tippt man wie im Büro: „25“, „12,5“ – höchstens zwei
//    Nachkommastellen, höchstens 99999 (Vertrag, BUERO_GRENZEN.menge).
//
// Die Arbeitskopie (Material dazu, Menge ändern, entfernen) steht in
// arbeitskopie.ts, wie alles, was die Besichtigung ändert.

import { BUERO_GRENZEN, istKennung, standardOrdnen, type DraussenMaterialAntwort, type DraussenMaterialZeile } from "@happy-energy/kern";
import { ApiFehler, fehlerMelden, holen, OhneVerbindung, ohneNetz } from "@happy-energy/oberflaeche";
import { speicher, type Materialangabe, type StandardEintrag, type Station } from "./speicher.ts";
import type { Bearbeiten, NeuesMaterial } from "./arbeitskopie.ts";

// --- Die Standardliste am Gerät ----------------------------------------------------------

/** Ist das ein Eintrag, wie der Server ihn schickt? Was nicht passt, kommt nicht aufs Gerät. */
function gueltig(z: unknown): z is DraussenMaterialZeile {
  if (!z || typeof z !== "object") return false;
  const e = z as Record<string, unknown>;
  return (
    istKennung(e["id"]) &&
    typeof e["bezeichnung"] === "string" &&
    typeof e["einheit"] === "string" &&
    typeof e["menge"] === "number" &&
    Number.isFinite(e["menge"]) &&
    (e["artikelnummer"] === null || typeof e["artikelnummer"] === "string") &&
    (e["gruppe"] === null || typeof e["gruppe"] === "string")
  );
}

/**
 * Holt die Liste vom Server und legt sie am Gerät ab – sie ersetzt die
 * vorige ganz (was das Büro herausgenommen hat, ist dann weg). Gibt zurück,
 * wie viele Einträge es sind. Fehler (kein Netz, abgewiesen) wirft es weiter.
 *
 * Seit E3c (S5): `firmaId` – die Firma der Anmeldung; mit ihr liegt neben der
 * Liste die Adresse von K2 am Gerät (`firma`). Ohne: nur die Liste.
 */
export async function standardlisteHolen(firmaId?: string): Promise<number> {
  const antwort = await holen<DraussenMaterialAntwort>("/api/doku/standardmaterial");
  // Keine Liste (leerer Körper, fremdes JSON): die alte bleibt – eine leere Antwort ist keine leere Liste.
  if (!Array.isArray(antwort?.eintraege)) throw new Error("Die Standardliste kam ohne Inhalt – die alte bleibt.");
  const eintraege = antwort.eintraege.filter(gueltig).map((e) => ({ ...e }));
  const k2Adresse = typeof antwort.k2Adresse === "string" ? antwort.k2Adresse : null;
  await speicher.transaction("rw", speicher.standardliste, speicher.firma, async () => {
    await speicher.standardliste.clear();
    await speicher.standardliste.bulkPut(eintraege);
    if (firmaId !== undefined) await speicher.firma.put({ id: "firma", firmaId, k2Adresse });
  });
  return eintraege.length;
}

/** Was am Gerät liegt – in der Reihenfolge des Servers (nach Gruppe, dann Bezeichnung). */
export async function standardlisteLesen(): Promise<StandardEintrag[]> {
  return listeOrdnen(await speicher.standardliste.toArray());
}

/**
 * Nach Gruppe (ohne Gruppe: nach Bezeichnung), dann Bezeichnung – seit dem
 * Augentest E3b mit Zahlen der Reihe nach („40×40“ vor „100×100“), dieselbe
 * Regel wie im Büro (Kern, standard-ordnung.ts).
 */
export function listeOrdnen<T extends Pick<StandardEintrag, "gruppe" | "bezeichnung" | "id">>(liste: readonly T[]): T[] {
  return standardOrdnen(liste);
}

/** Wann zuletzt geholt – nicht öfter als einmal je Minute. */
const TAKT = 60_000;
let zuletzt = 0;
let laeuft = false;

/**
 * Stößt das Holen an – beim Öffnen der Startseite, der Seite „Material“ und
 * (seit E3c, S5) der Dachplanung, nicht öfter als einmal je Minute, nie
 * zweimal zugleich, ohne Netz gar nicht. Fehler bleiben still (die alte
 * Liste gilt weiter); nur Unerwartetes geht an die Fehlerwache.
 */
export function standardlisteAnstossen(firmaId: string, jetzt = Date.now(), danach?: () => void): void {
  if (laeuft || ohneNetz() || jetzt - zuletzt < TAKT) return;
  zuletzt = jetzt;
  laeuft = true;
  void standardlisteHolen(firmaId)
    .then(() => danach?.())
    .catch((fehler: unknown) => {
      if (fehler instanceof OhneVerbindung) {
        zuletzt = 0;
        return;
      }
      if (fehler instanceof ApiFehler) return;
      fehlerMelden("doku", new Error(`Standardliste holen fehlgeschlagen – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
    })
    .finally(() => {
      laeuft = false;
    });
}

// --- Die Firma am Gerät (seit E3c, S5; O86) -------------------------------------------------
//
// Was am Gerät für die Firma liegt – die Standardliste und die Adresse von
// K2 – gilt nur für die Firma, für die es geholt wurde. Meldet sich jemand
// ab oder einer anderen Firma an, kommt es weg: Sonst böte ein Gerät ohne
// Netz Material der alten Firma an, das der Server abweist, und der Eintrag
// hinge im Postausgang (Code-Prüfung E3b, Befund 7). Holt die App es wieder,
// liegt es unter der neuen Firma da.

/**
 * Beim Start der App: Gehört, was am Gerät liegt, zu dieser Firma? Sonst –
 * auch, wenn noch nie eine Firma vermerkt war (ein Gerät von vor dem
 * Update) – kommen Liste und Adresse weg, und die Firma wird vermerkt. Gibt
 * zurück, ob etwas weggeräumt wurde.
 */
export async function firmaPruefen(firmaId: string): Promise<boolean> {
  return speicher.transaction("rw", speicher.standardliste, speicher.firma, async () => {
    const w = await speicher.firma.get("firma");
    if (w?.firmaId === firmaId) return false;
    await speicher.standardliste.clear();
    await speicher.firma.put({ id: "firma", firmaId, k2Adresse: null });
    return true;
  });
}

/** Beim Abmelden: Liste und Adresse vom Gerät – der Nächste holt sie für seine Firma neu. */
export async function firmaVergessen(): Promise<void> {
  zuletzt = 0;
  await speicher.transaction("rw", speicher.standardliste, speicher.firma, async () => {
    await speicher.standardliste.clear();
    await speicher.firma.clear();
  });
}

/** Die Adresse von K2 am Gerät – nur, wenn sie für diese Firma geholt wurde; sonst null. */
export async function k2AdresseLesen(firmaId: string): Promise<string | null> {
  const w = await speicher.firma.get("firma");
  return w?.firmaId === firmaId ? w.k2Adresse : null;
}

// --- Kacheln ------------------------------------------------------------------------------

/** Eine Kachel zum Antippen: ein Eintrag – oder eine Gruppe mit ihren Varianten. */
export interface Kachel {
  /** Zum Wiederfinden in React: die Gruppe oder die Kennung des Eintrags. */
  schluessel: string;
  /** Was auf der Kachel steht: der Name der Gruppe oder die Bezeichnung. */
  titel: string;
  /** Die Einheit, wenn alle Varianten dieselbe haben („m“) – sonst null. */
  einheit: string | null;
  /** Ein Eintrag allein: genau einer; eine Gruppe: ihre Varianten. */
  varianten: StandardEintrag[];
}

/**
 * Die Kacheln aus der Liste: Einträge mit derselben Gruppe (ohne Rücksicht
 * auf Groß- und Kleinschreibung und Leerzeichen am Rand) werden eine Kachel,
 * an der Stelle ihres ersten Eintrags. Eine Gruppe mit nur einem Eintrag ist
 * eine Kachel mit seiner Bezeichnung.
 */
export function kacheln(liste: readonly StandardEintrag[]): Kachel[] {
  const ergebnis: Kachel[] = [];
  const gruppen = new Map<string, Kachel>();
  for (const e of liste) {
    const gruppe = e.gruppe?.trim();
    if (!gruppe) {
      ergebnis.push({ schluessel: e.id, titel: e.bezeichnung, einheit: e.einheit, varianten: [e] });
      continue;
    }
    const k = gruppe.toLocaleLowerCase("de-AT");
    const da = gruppen.get(k);
    if (da) {
      da.varianten.push(e);
      if (da.einheit !== e.einheit) da.einheit = null;
    } else {
      const neu: Kachel = { schluessel: `gruppe:${k}`, titel: gruppe, einheit: e.einheit, varianten: [e] };
      gruppen.set(k, neu);
      ergebnis.push(neu);
    }
  }
  // Eine Gruppe mit einem einzigen Eintrag sagt, was sie ist.
  return ergebnis.map((k) => (k.varianten.length === 1 ? { ...k, titel: k.varianten[0]!.bezeichnung } : k));
}

// --- Mengen -----------------------------------------------------------------------------------

/**
 * Liest eine Menge, wie Menschen sie tippen: „25“, „12,5“ oder „12.5“, auch
 * mit der Einheit dahinter („25 m“). Leer: null. Ungültig: undefined.
 */
export function mengeLesen(eingabe: string): number | null | undefined {
  const roh = eingabe
    .trim()
    .replace(/\s*[a-zäöüß.²³]+$/i, "")
    .replace(",", ".");
  if (eingabe.trim() === "") return null;
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(roh)) return undefined;
  const zahl = Number(roh);
  return Number.isFinite(zahl) && zahl >= 0 && zahl <= BUERO_GRENZEN.menge ? zahl : undefined;
}

/** Eine Menge zum Anzeigen: 12.5 → „12,5“. */
export function mengeZeigen(menge: number): string {
  return String(Math.round(menge * 100) / 100).replace(".", ",");
}

/** Die Menge, mit der eine angetippte Variante beginnt: die Vorgabe der Liste – 0 heißt „noch keine“ (das Feld bleibt leer). */
export function vorgabeText(e: Pick<StandardEintrag, "menge">): string {
  return e.menge > 0 ? mengeZeigen(e.menge) : "";
}

// --- An der Besichtigung ------------------------------------------------------------------------

/** Das Material an dieser Stelle: an einer Station (Kennung) – oder auf der Seite „Material“ (null). */
export function materialAn(liste: readonly Materialangabe[], stationId: string | null): Materialangabe[] {
  return liste.filter((m) => m.stationId === stationId);
}

/**
 * Die Marke eines Materials – wie bei den Längen: im Nachtrag „neu“ oder
 * „berichtigt“, beim Nachsehen „nachgereicht“ oder „berichtigt“.
 */
export function materialMarke(m: Pick<Materialangabe, "nachtragId" | "geaendertImNachtrag">, modus: Bearbeiten): string | null {
  if (modus.art === "nachtrag") return m.nachtragId === modus.nachtragId ? "neu" : m.geaendertImNachtrag === modus.nachtragId ? "berichtigt" : null;
  if (modus.art === "gesperrt") return m.geaendertImNachtrag ? "berichtigt" : m.nachtragId ? "nachgereicht" : null;
  return null;
}

/** Lässt sich dieses Material entfernen? Beim Aufnehmen alles, im Nachtrag nur seins – sonst nicht (0 eintragen). */
export function entfernbar(m: Pick<Materialangabe, "nachtragId">, modus: Bearbeiten): boolean {
  if (modus.art === "aufnahme") return true;
  return modus.art === "nachtrag" && m.nachtragId === modus.nachtragId;
}

/**
 * Lässt sich der Text (Bezeichnung, Einheit) ändern? Nur bei eigenen
 * Angaben („Etwas anderes“) und nur, wo sie sich auch entfernen ließen – bei
 * der Standardliste kommt der Name vom Büro, und was schon im Büro ist,
 * bleibt, wie es ist (Gernot, Augentest E3b, 23.10.2026).
 */
export function textAenderbar(m: Pick<Materialangabe, "nachtragId" | "standardId">, modus: Bearbeiten): boolean {
  return m.standardId === null && entfernbar(m, modus);
}

/** Kurz, was eingetragen ist: „Solarkabel 6 mm² 25 m“. */
export function materialText(m: Pick<Materialangabe, "bezeichnung" | "menge" | "einheit">): string {
  return `${m.bezeichnung} ${mengeZeigen(m.menge)} ${m.einheit}`;
}

/**
 * Wie eine Variante auf ihrem Knopf heißt: ohne den Namen der Gruppe vorne
 * („Kabelkanal 40×60“ in der Gruppe „Kabelkanal“ → „40×60“) – so passen
 * drei Größen nebeneinander. Bliebe nichts übrig, die ganze Bezeichnung.
 */
export function variantenName(e: Pick<StandardEintrag, "bezeichnung">, gruppe: string): string {
  const g = gruppe.trim();
  const b = e.bezeichnung.trim();
  if (!g || b.length <= g.length || b.slice(0, g.length).toLocaleLowerCase("de-AT") !== g.toLocaleLowerCase("de-AT")) return b;
  const rest = b
    .slice(g.length)
    .replace(/^[\s,:–-]+/, "")
    .trim();
  return rest || b;
}

/** Was im Formular „Dazu“ steht, bevor es Material wird. */
export interface MaterialFormular {
  /** Die gewählte Variante der Standardliste – null: „Etwas anderes“. */
  eintrag: StandardEintrag | null;
  /** Nur bei „Etwas anderes“: frei getippt. */
  bezeichnung: string;
  einheit: string;
  /** Wie getippt: „25“, „12,5“. */
  menge: string;
}

/**
 * Prüft das Formular „Dazu“: Bei der Standardliste kommen Bezeichnung,
 * Einheit und Artikelnummer aus dem Eintrag (der Server nimmt sie ohnehin
 * aus seiner Liste), bei „Etwas anderes“ sind Bezeichnung und Einheit
 * Pflicht. Die Menge ist Pflicht und größer als 0 – „0“ ist nur zum
 * Berichtigen da, wenn etwas doch nicht gebraucht wird. Gibt das neue
 * Material zurück – oder, was fehlt, in einem Satz.
 */
export function formularPruefen(f: MaterialFormular, stationId: string | null): { ok: true; neu: NeuesMaterial } | { ok: false; meldung: string } {
  const menge = mengeLesen(f.menge);
  if (f.eintrag === null) {
    const bezeichnung = f.bezeichnung.trim();
    if (!bezeichnung) return { ok: false, meldung: "Was ist es? Bitte eine Bezeichnung." };
    if (bezeichnung.length > BUERO_GRENZEN.bezeichnung) return { ok: false, meldung: `Die Bezeichnung ist zu lang – höchstens ${BUERO_GRENZEN.bezeichnung} Zeichen.` };
    if (!f.einheit.trim()) return { ok: false, meldung: "Bitte eine Einheit, z. B. Stk oder m." };
    if (f.einheit.trim().length > BUERO_GRENZEN.einheit) return { ok: false, meldung: `Die Einheit ist zu lang – höchstens ${BUERO_GRENZEN.einheit} Zeichen.` };
  }
  if (menge === null || menge === 0) return { ok: false, meldung: "Bitte eine Menge eintragen." };
  if (menge === undefined) return { ok: false, meldung: "Bitte eine Zahl, z. B. 25 oder 12,5." };
  const neu: NeuesMaterial = f.eintrag
    ? {
        stationId,
        standardId: f.eintrag.id,
        artikelnummer: f.eintrag.artikelnummer,
        bezeichnung: f.eintrag.bezeichnung,
        einheit: f.eintrag.einheit,
        menge,
      }
    : { stationId, standardId: null, artikelnummer: null, bezeichnung: f.bezeichnung.trim(), einheit: f.einheit.trim(), menge };
  return { ok: true, neu };
}

/**
 * Die Reihenfolge auf der Seite „Material“: nach Station (Dach zuerst), was
 * auf der Seite selbst eingetragen ist, zuletzt – darin, wie es dazukam. So
 * zeigt es auch der Server.
 */
export function materialOrdnen<M extends Pick<Materialangabe, "stationId" | "nr">>(liste: readonly M[], stationen: readonly Pick<Station, "id" | "nr">[]): M[] {
  const nrVon = new Map(stationen.map((s) => [s.id, s.nr]));
  const rang = (m: M) => (m.stationId === null ? Number.MAX_SAFE_INTEGER : (nrVon.get(m.stationId) ?? Number.MAX_SAFE_INTEGER - 1));
  return [...liste].sort((a, b) => rang(a) - rang(b) || a.nr - b.nr);
}

/**
 * Längen gibt es in der Doku-App seit E3b nur noch, wo eine Besichtigung
 * schon welche hat (vor dem Update begonnen, oder eine Kopie vom Server mit
 * Längen) – dort geht es weiter wie gewohnt, auch im Nachtrag. Neue
 * Besichtigungen geben Kabel, Rohr und Kabelkanal als Material an (Gernot,
 * 22.10.2026). Eine geleerte Länge zählt mit: Ihre Kennung ist da.
 */
export function mitLaengen(laengen: readonly unknown[]): boolean {
  return laengen.length > 0;
}
